import React from 'react';
import { Copy, Edit } from 'lucide-react';
import ImportedExpandedRows from './ExpandedRows';
import { copyToClipboard } from '../utils/copyToClipboard';

function subscriptionBadge(subscription_status) {
  if (subscription_status === 'active')   return { cls: 'bg-success',          label: 'Subscribed' };
  if (subscription_status === 'expired')  return { cls: 'bg-warning text-dark', label: 'Expired'    };
  if (subscription_status === 'inactive') return { cls: 'bg-secondary',         label: 'Inactive'   };
  return                                         { cls: 'bg-secondary',         label: 'Unsubscribed' };
}

/**
 * Local wrapper so we can pass new props while keeping the imported implementation.
 * Expects `entry` to be the full userDetails object from the parent row.
 */
function ExpandedRows({ entry, childProps, getStatusBadge }) {
  const { userCode, allEntries } = entry || {};
  return (
    <ImportedExpandedRows
      userCode={userCode}
      userPasswords={allEntries}
      withMultilogin={childProps?.withMultilogin}
      getStatusBadge={getStatusBadge}
      {...childProps}
    />
  );
}

/**
 * Table
 * Renders the main grid of user-code "parent" rows,
 * expandable to show child password rows via <ExpandedRows />.
 */
export default function Table({
  entries,
  expandedUserCode,
  setExpandedUserCode,
  notesOpenMap,
  toggleNotes,
  sortField,
  sortDirection,
  onSortSubscribed,
  onSortOnboarded,
  editingMainRow,
  mainRowEditDraft,
  setMainRowEditDraft,
  onStartEditingMainRow,
  onSaveMainRow,
  onCancelMainRow,
  withMultilogin,
  getStatusBadge, // ✅
  childProps,
  subscribedCount,
}) {
  const headerCols = [
    { key: 'userCode', label: 'User Code' },
    { key: 'fullName', label: 'Full Name' },
    ...(withMultilogin ? [{ key: 'multilogin', label: 'Multilogin' }] : []),
    { key: 'subscribed', label: `Subscribed${subscribedCount != null ? ` (${subscribedCount})` : ''}`, sortable: true, onSort: onSortSubscribed },
    { key: 'onboarded', label: 'Onboarded', sortable: true, onSort: onSortOnboarded },
    { key: 'slackId', label: 'Slack ID' },
    { key: 'emailsCount', label: 'Emails Count' },
    { key: 'numbersCount', label: 'Numbers Count' },
    { key: 'crmPassword', label: '@Password' },
    { key: 'referral', label: 'Referral' },
    { key: 'dob', label: 'DOB' },
    { key: 'actions', label: 'Actions' },
  ];

  const renderHeader = () => (
    <thead>
      <tr>
        {headerCols.map((col) => {
          if (!col.sortable) return <th key={col.key}>{col.label}</th>;
          const isActive = sortField === col.key;
          return (
            <th key={col.key} style={{ cursor: 'pointer' }} onClick={col.onSort}>
              {col.label} {isActive && <small>{sortDirection === 'desc' ? ' ▼' : ' ▲'}</small>}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const renderParentRow = (userDetails) => {
    const {
      userCode,
      totalEntries,
      emailsCount, // unique email count
      numbersCount,
      slackId,
      referral,
      crm_password,
      notes,
      subscribed,
      subscription_status,
      onboarded,
      multilogin,
      allEntries,
      dob,
    } = userDetails;

    const isExpanded = expandedUserCode === userCode;
    const isNotesOpen = notesOpenMap[userCode] || false;
    const hasNotes = Boolean(notes && notes.trim().length > 0);
    const isEditing = editingMainRow === userCode;

    const firstEmailRecord = allEntries[0];
    const displayName = firstEmailRecord?.full_name || userCode;

    const notesButtonClass = hasNotes ? 'btn btn-primary btn-sm' : 'btn btn-outline-primary btn-sm';

    if (isEditing) {
      return (
        <tr key={userCode}>
          <td>{userCode}</td>

          {/* Full Name */}
          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              value={mainRowEditDraft.fullName}
              onChange={(e) => setMainRowEditDraft((p) => ({ ...p, fullName: e.target.value }))}
            />
          </td>

          {/* Multilogin (optional) */}
          {withMultilogin && (
            <td>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={!!mainRowEditDraft.multilogin}
                  onChange={(e) => setMainRowEditDraft((p) => ({ ...p, multilogin: e.target.checked }))}
                />
              </div>
            </td>
          )}

          <td>
            {(() => { const b = subscriptionBadge(subscription_status); return (
              <span className={`badge ${b.cls}`}>{b.label}</span>
            ); })()}
          </td>
          <td>
            <span className={`badge ${onboarded ? 'bg-success' : 'bg-secondary'}`}>Onboarded</span>
          </td>

          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              value={mainRowEditDraft.slackId}
              onChange={(e) => setMainRowEditDraft((p) => ({ ...p, slackId: e.target.value }))}
            />
          </td>

          <td>{emailsCount ?? totalEntries}</td>

          <td>
            <span className="badge bg-info">{numbersCount || 0}</span>
          </td>

          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              value={mainRowEditDraft.crm_password}
              onChange={(e) => setMainRowEditDraft((p) => ({ ...p, crm_password: e.target.value }))}
            />
          </td>

          <td>
            <input
              type="text"
              className="form-control form-control-sm"
              value={mainRowEditDraft.referral}
              onChange={(e) => setMainRowEditDraft((p) => ({ ...p, referral: e.target.value }))}
            />
          </td>

          <td>
            <input
              type="date"
              className="form-control form-control-sm"
              value={mainRowEditDraft.dob || ''}
              onChange={(e) => setMainRowEditDraft((p) => ({ ...p, dob: e.target.value }))}
            />
          </td>

          <td>
            <button className="btn btn-primary btn-sm me-2" onClick={() => onSaveMainRow(userCode)}>
              Save
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onCancelMainRow}>
              Cancel
            </button>
          </td>
        </tr>
      );
    }

    const entry = userDetails;

    return (
      <React.Fragment key={userCode}>
        <tr
          className={`cursor-pointer ${isExpanded ? 'expanded-parent' : ''}`}
          onClick={() => setExpandedUserCode(isExpanded ? null : userCode)}
        >
          <td>{userCode}</td>
          <td>{displayName}</td>

          {withMultilogin && (
            <td>
              <div className="form-check form-switch m-0">
                <input className="form-check-input" type="checkbox" checked={!!multilogin} readOnly />
              </div>
            </td>
          )}

          <td>
            {(() => { const b = subscriptionBadge(subscription_status); return (
              <span className={`badge ${b.cls}`}>{b.label}</span>
            ); })()}
            {sortField === 'subscribed' && <small>{sortDirection === 'desc' ? ' ▼' : ' ▲'}</small>}
          </td>

          <td>
            <span className={`badge ${onboarded ? 'bg-success' : 'bg-secondary'}`}>Onboarded</span>
            {sortField === 'onboarded' && <small>{sortDirection === 'desc' ? ' ▼' : ' ▲'}</small>}
          </td>

          <td>
            {slackId && (
              <>
                {slackId}{' '}
                <button
                  className="btn btn-link btn-sm p-0 ms-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(slackId);
                  }}
                  title="Copy Slack ID"
                >
                  <Copy size={14} />
                </button>
              </>
            )}
          </td>

          <td>{emailsCount ?? totalEntries}</td>

          <td>
            <span className="badge bg-info">{numbersCount || 0}</span>
          </td>

          <td>
            {crm_password && (
              <>
                {crm_password}{' '}
                <button
                  className="btn btn-link btn-sm p-0 ms-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(crm_password);
                  }}
                  title="Copy @Password"
                >
                  <Copy size={14} />
                </button>
              </>
            )}
          </td>

          <td>{referral}</td>

          <td>{dob || '—'}</td>

          <td>
            <button
              className={notesButtonClass}
              onClick={(e) => {
                e.stopPropagation();
                toggleNotes(userCode);
              }}
            >
              Notes
            </button>
            <button
              className="btn btn-outline-secondary btn-sm ms-2"
              onClick={(e) => {
                e.stopPropagation();
                onStartEditingMainRow(userDetails);
              }}
              title="Edit main row"
            >
              <Edit size={14} />
            </button>
          </td>
        </tr>

        {isExpanded && (
            <tr className="expanded-child">
              {/* colSpan stays the same because this is spanning the parent table's columns */}
              <td colSpan={withMultilogin ? 12 : 11}>
                <div className="expanded-content p-3">
                  <table className="table table-sm mb-0" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th>User Code</th>
                        <th>Full Name</th>
                        {withMultilogin && <th>Multilogin</th>}
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Site</th>
                        <th>Password</th>
                        <th>Proxy</th> {/* <-- NEW to match ChildRows */}
                        {childProps?.showPersonaSelfTransfer && <th>Persona Check</th>}
                        {childProps?.showPersonaSelfTransfer && <th>Self Transfer</th>}
                        <th>Address</th>
                        <th>DOB</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <ExpandedRows
                        entry={entry}
                        childProps={{ ...childProps, withMultilogin }}
                        getStatusBadge={getStatusBadge}
                      />
                    </tbody>
                  </table>

                  {isNotesOpen && (
                    <div className="mt-2 p-2" style={{ backgroundColor: '#fefccf', borderRadius: 4 }}>
                      <h6>Notes for {userCode}</h6>
                      <textarea
                        className="form-control"
                        rows="3"
                        defaultValue={notes || ''}
                        onBlur={(e) => childProps?.onSaveNotes?.(userCode, e.target.value)}
                      />
                      <small className="text-muted">Changes save on blur.</small>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          )}
      </React.Fragment>
    );
  };

  return (
    <table className="table table-hover">
      {renderHeader()}
      <tbody>{entries.map(renderParentRow)}</tbody>
    </table>
  );
}
